import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Download, X, ThumbsUp, ThumbsDown, Share2, RefreshCw, AlertTriangle, Lightbulb, BarChart3 } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import NexiOrb from "./NexiOrb";
import ChartRenderer, { parseChartFromContent } from "./ChartRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Parse and render markdown content properly
 * Fixes raw ** and other markdown characters showing to users
 */
const MarkdownRenderer = ({ content }) => {
  const renderMarkdown = useMemo(() => {
    if (!content) return null;
    
    // Split by code blocks first to preserve them
    const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = codeBlockRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: "text", content: content.substring(lastIndex, match.index) });
      }
      parts.push({ type: "code", language: match[1] || "text", content: match[2].trim() });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < content.length) {
      parts.push({ type: "text", content: content.substring(lastIndex) });
    }
    
    if (parts.length === 0) {
      parts.push({ type: "text", content });
    }
    
    return parts.map((part, idx) => {
      if (part.type === "code") {
        return <CodeBlock key={idx} code={part.content} language={part.language} />;
      }
      return <TextContent key={idx} content={part.content} />;
    });
  }, [content]);
  
  return <div className="space-y-4">{renderMarkdown}</div>;
};

/**
 * Render text with proper markdown formatting
 */
const TextContent = ({ content }) => {
  if (!content?.trim()) return null;
  
  // Process the content line by line for better control
  const lines = content.split('\n');
  const elements = [];
  let currentList = null;
  let currentListType = null;
  
  lines.forEach((line, lineIdx) => {
    const trimmedLine = line.trim();
    
    // Headers
    if (trimmedLine.startsWith('### ')) {
      if (currentList) { elements.push(currentList); currentList = null; }
      elements.push(
        <h3 key={lineIdx} className="text-lg font-semibold text-cyan-300 mt-5 mb-2">
          {formatInlineMarkdown(trimmedLine.slice(4))}
        </h3>
      );
      return;
    }
    if (trimmedLine.startsWith('## ')) {
      if (currentList) { elements.push(currentList); currentList = null; }
      elements.push(
        <h2 key={lineIdx} className="text-xl font-semibold text-violet-300 mt-6 mb-3 border-b border-violet-500/20 pb-2">
          {formatInlineMarkdown(trimmedLine.slice(3))}
        </h2>
      );
      return;
    }
    if (trimmedLine.startsWith('# ')) {
      if (currentList) { elements.push(currentList); currentList = null; }
      elements.push(
        <h1 key={lineIdx} className="text-2xl font-bold text-slate-50 mt-6 mb-3 border-b border-violet-500/30 pb-2">
          {formatInlineMarkdown(trimmedLine.slice(2))}
        </h1>
      );
      return;
    }
    
    // Blockquotes
    if (trimmedLine.startsWith('> ')) {
      if (currentList) { elements.push(currentList); currentList = null; }
      elements.push(
        <blockquote key={lineIdx} className="border-l-4 border-fuchsia-500/60 pl-4 py-2 my-4 italic text-slate-300 bg-fuchsia-950/20 rounded-r-lg">
          {formatInlineMarkdown(trimmedLine.slice(2))}
        </blockquote>
      );
      return;
    }
    
    // Horizontal rule
    if (trimmedLine === '---' || trimmedLine === '***') {
      if (currentList) { elements.push(currentList); currentList = null; }
      elements.push(<hr key={lineIdx} className="my-6 border-slate-700/50" />);
      return;
    }
    
    // Unordered list items
    if (trimmedLine.match(/^[-*•]\s/)) {
      if (currentListType !== 'ul') {
        if (currentList) elements.push(currentList);
        currentList = <ul key={`ul-${lineIdx}`} className="space-y-2 my-3 pl-4 list-none">{[]}</ul>;
        currentListType = 'ul';
      }
      const items = [...(currentList.props.children || [])];
      items.push(
        <li key={lineIdx} className="flex items-start gap-2 text-slate-200">
          <span className="text-cyan-400 mt-1.5 text-xs">●</span>
          <span>{formatInlineMarkdown(trimmedLine.slice(2))}</span>
        </li>
      );
      currentList = <ul key={`ul-${lineIdx}`} className="space-y-2 my-3 pl-2 list-none">{items}</ul>;
      return;
    }
    
    // Ordered list items
    const orderedMatch = trimmedLine.match(/^(\d+)\.\s/);
    if (orderedMatch) {
      if (currentListType !== 'ol') {
        if (currentList) elements.push(currentList);
        currentList = <ol key={`ol-${lineIdx}`} className="space-y-2 my-3 pl-2 list-none">{[]}</ol>;
        currentListType = 'ol';
      }
      const items = [...(currentList.props.children || [])];
      items.push(
        <li key={lineIdx} className="flex items-start gap-3 text-slate-200">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs flex items-center justify-center font-mono">
            {orderedMatch[1]}
          </span>
          <span className="pt-0.5">{formatInlineMarkdown(trimmedLine.slice(orderedMatch[0].length))}</span>
        </li>
      );
      currentList = <ol key={`ol-${lineIdx}`} className="space-y-2 my-3 pl-2 list-none">{items}</ol>;
      return;
    }
    
    // Empty line - end list and add paragraph break
    if (!trimmedLine) {
      if (currentList) { elements.push(currentList); currentList = null; currentListType = null; }
      return;
    }
    
    // Regular paragraph
    if (currentList) { elements.push(currentList); currentList = null; currentListType = null; }
    elements.push(
      <p key={lineIdx} className="text-base text-slate-200 leading-relaxed mb-3">
        {formatInlineMarkdown(trimmedLine)}
      </p>
    );
  });
  
  // Don't forget any remaining list
  if (currentList) elements.push(currentList);
  
  return <>{elements}</>;
};

/**
 * Format inline markdown (bold, italic, code, links)
 * Also handles incomplete markdown during streaming
 */
const formatInlineMarkdown = (text) => {
  if (!text) return text;
  
  let processed = text;
  
  // Clean up incomplete markdown at the end (common during streaming)
  // Remove trailing **, *, `, _ that don't have a pair
  processed = processed.replace(/\*{1,3}$/, '');
  processed = processed.replace(/_+$/, '');
  processed = processed.replace(/`+$/, '');
  
  // Bold + Italic (***text***)
  processed = processed.replace(/\*\*\*([^*]+)\*\*\*/g, '###BOLDITALIC###$1###/BOLDITALIC###');
  // Bold (**text**) - Allow colon, comma, exclamation at the end
  processed = processed.replace(/\*\*([^*]+)\*\*/g, '###BOLD###$1###/BOLD###');
  processed = processed.replace(/__([^_]+)__/g, '###BOLD###$1###/BOLD###');
  // Italic (*text* or _text_)
  processed = processed.replace(/\*([^*\n]+)\*/g, '###ITALIC###$1###/ITALIC###');
  processed = processed.replace(/_([^_\n]+)_/g, '###ITALIC###$1###/ITALIC###');
  // Inline code (`code`)
  processed = processed.replace(/`([^`\n]+)`/g, '###CODE###$1###/CODE###');
  // Links [text](url)
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '###LINK###$1###URL###$2###/LINK###');
  
  // Convert tokens to React elements
  // Fixed regex: the '###' before /TAGNAME### needs to work for both LINK (which has ###URL###) and non-LINK tags
  const tokenRegex = /###(BOLDITALIC|BOLD|ITALIC|CODE|LINK)###(.+?)(?:###URL###(.+?))?###\/\1###/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  
  while ((match = tokenRegex.exec(processed)) !== null) {
    if (match.index > lastIndex) {
      parts.push(processed.substring(lastIndex, match.index));
    }
    
    const type = match[1];
    const content = match[2];
    const url = match[3];
    
    switch (type) {
      case 'BOLDITALIC':
        parts.push(<strong key={key++} className="font-bold italic text-cyan-200">{content}</strong>);
        break;
      case 'BOLD':
        parts.push(<strong key={key++} className="font-semibold text-cyan-200">{content}</strong>);
        break;
      case 'ITALIC':
        parts.push(<em key={key++} className="italic text-violet-200">{content}</em>);
        break;
      case 'CODE':
        parts.push(<code key={key++} className="px-1.5 py-0.5 rounded bg-violet-950/80 border border-violet-500/30 text-cyan-300 font-mono text-sm">{content}</code>);
        break;
      case 'LINK':
        parts.push(<a key={key++} href={url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">{content}</a>);
        break;
      default:
        parts.push(content);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < processed.length) {
    parts.push(processed.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

/**
 * Code block with syntax highlighting and copy
 */
const CodeBlock = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="my-5 rounded-xl border border-slate-800 bg-[#0A0D1B] overflow-hidden shadow-xl">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#12162D] border-b border-slate-800">
        <span className="text-xs font-mono text-slate-400">{language}</span>
        <button
          onClick={handleCopy}
          className="px-2.5 py-1 rounded bg-slate-800 hover:bg-violet-600 text-slate-200 hover:text-white transition-all text-xs font-sans flex items-center gap-1.5"
          data-testid="copy-code-btn"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto">
        <code className="text-sm text-emerald-400 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
};

/**
 * Structured content cards for special AI responses
 */
const WarningCard = ({ children }) => (
  <div className="my-5 p-4 rounded-xl border border-amber-500/40 bg-amber-950/20 flex items-start gap-3 shadow-[0_0_15px_rgba(245,158,11,0.15)]">
    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
    <div className="text-amber-200 text-sm">{children}</div>
  </div>
);

const TipCard = ({ children }) => (
  <div className="my-5 p-4 rounded-xl border border-cyan-500/40 bg-cyan-950/20 flex items-start gap-3 shadow-[0_0_15px_rgba(6,182,212,0.15)]">
    <Lightbulb className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
    <div className="text-cyan-200 text-sm">{children}</div>
  </div>
);

/**
 * Main MessageBubble Component - Redesigned
 */
const MessageBubbleRedesigned = ({ message, index, conversationId, userName }) => {
  const [copied, setCopied] = useState(false);
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [feedback, setFeedback] = useState(message.feedback || null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  
  const isUser = message.role === "user";
  const isStreaming = message.streaming || false;

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const shareText = `Check out this response from Nexi AI:\n\n"${message.content.substring(0, 200)}${message.content.length > 200 ? '...' : ''}"\n\nGet Nexi AI: https://apps.apple.com/us/app/nexi-ai/id6758422744`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Nexi AI Response',
          text: shareText,
          url: 'https://apps.apple.com/us/app/nexi-ai/id6758422744'
        });
        toast.success('Shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          navigator.clipboard.writeText(shareText);
          toast.success('Copied to clipboard - ready to share!');
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Copied to clipboard - ready to share!');
    }
  };

  const handleFeedback = async (type) => {
    if (submittingFeedback || feedback === type) return;
    
    setSubmittingFeedback(true);
    try {
      const response = await fetch(`${API}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_id: message.id,
          conversation_id: conversationId,
          feedback_type: type,
          message_content: message.content,
          user_query: message.userQuery || null,
          user_name: userName || null
        })
      });

      if (response.ok) {
        setFeedback(type);
        toast.success(type === 'up' ? 'Thanks for the feedback!' : 'Thanks! We\'ll improve.');
      }
    } catch (error) {
      console.error('Feedback error:', error);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const firstLine = message.content.split('\n')[0];
      const title = firstLine.replace(/^#+ /, '').substring(0, 50) || "Nexi_Document";
      
      const response = await fetch(`${API}/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: message.content, title })
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Nexi_Document.${format === 'word' ? 'docx' : format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      if (isIOS || isSafari) {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Downloaded as ${format.toUpperCase()}`);
      setShowDownloadPopup(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export`);
    } finally {
      setDownloading(null);
    }
  };

  // Determine orb state based on streaming/content
  const orbState = isStreaming 
    ? (message.content?.includes('Processing') ? 'file_analysis' : 'generating')
    : 'complete';

  // USER MESSAGE
  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20, x: 20 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="flex justify-end mb-4"
        data-testid={`user-message-${index}`}
      >
        <div 
          className="max-w-[85%] md:max-w-[70%] rounded-2xl rounded-tr-sm p-4 sm:p-5 
            bg-gradient-to-br from-violet-600 via-indigo-600 to-cyan-600 
            text-white shadow-[0_4px_20px_rgba(124,58,237,0.35)]
            border border-white/10"
        >
          {/* Attached Image */}
          {message.image && (
            <img 
              src={message.image} 
              alt="Uploaded" 
              className="max-w-full rounded-lg mb-3 border border-white/20" 
            />
          )}
          
          {/* File indicator */}
          {message.fileName && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-white/10 text-sm">
              <span>📎</span>
              <span className="truncate">{message.fileName}</span>
            </div>
          )}
          
          <p className="text-white text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
            {message.content}
          </p>
          
          <div className="mt-2 text-xs text-white/50 text-right font-mono">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </motion.div>
    );
  }

  // AI MESSAGE - Open editorial style
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="mb-6"
      data-testid={`ai-message-${index}`}
    >
      {/* AI Message Header */}
      <div className="flex items-center gap-3 mb-3">
        <NexiOrb state={orbState} size="sm" />
        <span className="text-xs font-mono text-cyan-400 tracking-wider uppercase">Nexi</span>
        {isStreaming && (
          <span className="text-xs text-violet-400 animate-pulse">thinking...</span>
        )}
      </div>
      
      {/* AI Message Content - Open layout, not boxed */}
      <div className="pl-10 sm:pl-12 max-w-4xl">
        {/* Render charts if present */}
        {parseChartFromContent(message.content).map((chart, idx) => (
          <ChartRenderer key={`chart-${idx}`} chartData={chart.chartData} />
        ))}
        
        {/* Main content with markdown rendering */}
        <MarkdownRenderer content={message.content?.replace(/```chart[\s\S]*?```/g, '').trim()} />
        
        {/* Related Images */}
        {message.relatedImages && message.relatedImages.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-800/60">
            <p className="text-slate-500 text-xs mb-3 flex items-center gap-2">
              <span>📷</span> Related Images
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {message.relatedImages.map((img, idx) => (
                <div key={idx} className="relative group cursor-pointer" onClick={() => window.open(img.url, '_blank')}>
                  <img
                    src={img.url}
                    alt={img.alt || 'Related image'}
                    className="w-full h-28 object-cover rounded-lg border border-slate-800 hover:border-violet-500/50 transition-all"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <span className="text-white text-xs">View</span>
                  </div>
                </div>
              ))}
            </div>
            <a 
              href="https://www.pexels.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 text-slate-600 hover:text-slate-400 text-[10px]"
            >
              Photos by Pexels
            </a>
          </div>
        )}
        
        {/* Action Bar - Subtle, appears after streaming complete */}
        {!isStreaming && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-1.5 mt-5 pt-4 border-t border-slate-800/40"
          >
            <ActionButton icon={Copy} label="Copy" onClick={handleCopy} active={copied} activeIcon={Check} />
            <ActionButton icon={Download} label="Save" onClick={() => setShowDownloadPopup(true)} />
            <ActionButton icon={Share2} label="Share" onClick={handleShare} />
            
            <div className="ml-auto flex items-center gap-1">
              <ActionButton 
                icon={ThumbsUp} 
                onClick={() => handleFeedback('up')} 
                active={feedback === 'up'}
                activeColor="text-emerald-400"
                disabled={submittingFeedback}
              />
              <ActionButton 
                icon={ThumbsDown} 
                onClick={() => handleFeedback('down')} 
                active={feedback === 'down'}
                activeColor="text-rose-400"
                disabled={submittingFeedback}
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* Download Modal */}
      <AnimatePresence>
        {showDownloadPopup && (
          <DownloadModal
            onClose={() => setShowDownloadPopup(false)}
            onDownload={handleDownload}
            downloading={downloading}
            onCopy={handleCopy}
            copied={copied}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Action button component
 */
const ActionButton = ({ icon: Icon, label, onClick, active, activeIcon: ActiveIcon, activeColor = "text-emerald-400", disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all
      ${active 
        ? `${activeColor} bg-white/5` 
        : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
      }
      disabled:opacity-50`}
    data-testid={`action-${label?.toLowerCase() || 'btn'}`}
  >
    {active && ActiveIcon ? <ActiveIcon size={14} /> : <Icon size={14} />}
    {label && <span className="hidden sm:inline">{label}</span>}
  </button>
);

/**
 * Download Modal
 */
const DownloadModal = ({ onClose, onDownload, downloading, onCopy, copied }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      onClick={(e) => e.stopPropagation()}
      className="w-full sm:max-w-sm sm:mx-4 rounded-t-2xl sm:rounded-2xl 
        bg-[#0D1127] border-t sm:border border-violet-500/30 
        p-5 shadow-2xl safe-area-bottom"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Save Response</h3>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
          <X size={20} className="text-slate-400" />
        </button>
      </div>
      
      <div className="space-y-2">
        {[
          { format: 'word', icon: '📘', label: 'Word Document', ext: '.docx', color: 'blue' },
          { format: 'pdf', icon: '📕', label: 'PDF Document', ext: '.pdf', color: 'red' },
          { format: 'txt', icon: '📄', label: 'Plain Text', ext: '.txt', color: 'gray' }
        ].map(({ format, icon, label, ext, color }) => (
          <button
            key={format}
            onClick={() => onDownload(format)}
            disabled={downloading === format}
            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl 
              bg-${color}-500/10 border border-${color}-500/30 
              hover:bg-${color}-500/20 transition-colors`}
          >
            <span className="text-xl">{icon}</span>
            <div className="text-left flex-1">
              <span className="text-white block">{downloading === format ? 'Preparing...' : label}</span>
              <span className="text-slate-500 text-xs">{ext}</span>
            </div>
          </button>
        ))}
      </div>
      
      <button
        onClick={onCopy}
        className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 
          bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors"
      >
        {copied ? <Check size={18} className="text-emerald-400" /> : <Copy size={18} className="text-slate-400" />}
        <span className="text-slate-300">{copied ? 'Copied!' : 'Copy to clipboard'}</span>
      </button>
    </motion.div>
  </motion.div>
);

export default MessageBubbleRedesigned;
